/**
 * Roof face solver. 2D-first, per the architecture: each non-gable boundary edge
 * carries a PLANE rising inward from the bearing line at its slope. Since the
 * 2026-08-10 round the face regions come from a WEIGHTED STRAIGHT SKELETON
 * (straightSkeleton.ts — per-edge front speeds 1/m_i, gable edges static), so
 * non-convex footprints (L/T/plus) auto-resolve with TRUE VALLEYS. For convex
 * footprints the skeleton is exactly the old LOWER ENVELOPE of the planes (the
 * z-level sets are intersections of half-planes), so existing roofs solve
 * identically. The envelope solve — clipping the eave polygon with the half-planes
 * z_i ≤ z_j — remains as the FALLBACK for degenerate inputs (zero slopes on a
 * sloped roof, collinear slope breaks, spike corners, or any simulation path that
 * fails its tiling verification). The intent model is untouched: per-edge
 * slopeRise/gable/overhangFt semantics and every RoofFace field are exactly as
 * before — only the region solver changed.
 *
 * Gable edges contribute NO plane (vertical end); their rake line is simply the eave
 * ring edge, and adjacent planes run through it. In the skeleton they are static
 * fronts that sweep no face — neighbors slide along them, which is what extends a
 * ridge to the gable line.
 *
 * BEARING-LINE SEMANTICS (ROOF round, 2026-08 — Matt's bearing-line insight): the
 * boundary is the BEARING line (drawn at the interior face of stud) and the solved
 * plane is the BEARING plane — the roof's UNDERSIDE, where the birdsmouth seats on
 * the plate. z datum: z(p) = zPlateFt + (slopeRise/12) · signedDistanceFromBearing-
 * Line(p, inward+). At the eave (overhang outward) the underside droops below the
 * plate, as built. Assembly thickness stacks UP from this plane (generate3d) — never
 * centered, never down — so changing the roof build-up never moves the plate.
 * Walls-attach-to-roof clips to these planes directly, landing wall tops at true
 * plate geometry.
 */
import type { Path2D, PhaseId, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { computeSkeletonFaceRings } from './straightSkeleton'
import { applyCornerTucks } from './cornerLap'

export interface RoofEdgeSpec {
  a: Pt2
  b: Pt2
  slopeRise: number
  overhangFt: number
  gable: boolean
  /**
   * ROOF ROUND E1: eave-zone structure depth in INCHES (per-edge node attr over the
   * TYPE standard; null = no crop). Pure PASSENGER data — the solver never reads it,
   * it only rides the spec through winding normalization so generate3d's eave crop
   * resolves against the face's TRUE bearing edge. Solved planes are byte-identical
   * with or without it (the walls-attach invariant).
   */
  eaveStructureDepthIn?: number | null
  /** ROOF ROUND E2: decorative rafter tails on this eave edge — passenger like
   *  eaveStructureDepthIn above; the solver never reads it. */
  tails?: boolean
  /** ROOF ROUND E3: fascia/gutter on this edge (per-edge node attr over the TYPE
   *  defaults) — passengers; consumed by trim.ts, which matches the skeleton's
   *  classified perimeter edges back to these specs GEOMETRICALLY (a rake belongs
   *  to its GABLE edge, which solves no face — so face passengers can't carry it). */
  fascia?: boolean
  gutter?: boolean
  /** ROOF ROUND E4: rake outlookers + barge rafter on this GABLE edge's rakes —
   *  passenger like fascia/gutter above; consumed by framing.ts (members) and
   *  trim.ts (fascia suppression on a barge rake). No-op on non-gable edges. */
  barge?: boolean
  /** FRIEZE BOARD (2026-10-07): a frieze board on this tails eave — passenger for framing.ts */
  frieze?: boolean
  /** FLAT SOFFIT + CORBELS round (2026-10-07): a flat soffit on this eave edge (per-edge
   *  node attr over the instance soffitStyle) — passenger; consumed by soffit.ts via
   *  the face. No-op on gable edges. */
  flatSoffit?: boolean
  /** ROOF BEARING WALLS (2026-10-05): how far the wall under this edge reaches past the
   *  bearing line to its FINISHED outside face — passenger, so the show members cap their
   *  cuts to the part outside the wall. Since 2026-10-12 the overhang adds only the reach
   *  to the outside face of STUD (roofBearingWallFt), so a zero eave stops at the framing
   *  and this can sit a little past the roof edge. */
  wallFt?: number
}

export interface RoofFace {
  /**
   * Index into the CALLER's boundary edge list (edge i = boundary[i] → boundary[i+1]
   * of the INPUT ring, so it indexes `solved.specs` / `boundary.nodes` directly).
   * 2026-08-19: solveRoofFaces now remaps this back through its CW→CCW winding
   * normalization before returning — previously it leaked the solver's NORMALIZED
   * index space, which on CW-drawn boundaries differs by (n−2−k) mod n and bit
   * three separate consumers (the E4 rake mapping, the E5 r3 tails merge, the
   * removed roof-joint grips writing the gable flag to the WRONG edge).
   */
  edgeIndex: number
  /** plan region of the face (same winding as the eave ring) */
  ring: Pt2[]
  /** rise per foot of run for this face (slopeRise / 12) */
  m: number
  /** the face's BEARING (underside) plane: z at a plan point */
  zAt: (p: Pt2) => number
  /** a point on this face's bearing line (at zAt = the plate elevation) */
  origin: Pt2
  /** unit plan direction of ASCENT (the bearing edge's inward normal) */
  uphill: Pt2
  /**
   * ROOF ROUND E1: the resolved eave-zone structure depth for THIS face's eave, in
   * inches (null = full depth, no crop). Consumed by generate3d only — the plane
   * itself never changes. A rake overhang beyond a gable line belongs to the
   * adjoining sloped face's ring, so it thins by that face's value.
   */
  eaveDepthIn?: number | null
  /** ROOF ROUND E2: decorative rafter tails on this face's eave edge (passenger —
   *  consumed by the framing generator + the solid's decking-read eave crop). */
  tails?: boolean
  /** ROOF ROUND E2: this face's resolved eave overhang in feet (passenger — the
   *  tail reach-in derives from it as 2× the eave depth). */
  overhangFt?: number
  /** ROOF BEARING WALLS: the bearing wall's reach past the line (part of overhangFt) */
  wallFt?: number
  /** FLAT SOFFIT + CORBELS round: this face's eave carries a flat soffit (passenger —
   *  soffit.ts derives the level region + elevation; framing.ts swaps tails for corbels) */
  flatSoffit?: boolean
  /** FRIEZE BOARD: this face's tails eave carries a frieze board (passenger — framing.ts) */
  frieze?: boolean
  /** REVERSED PITCH: this face falls inward from its bearing line (a negative slope) —
   *  its bearing edge is the HIGH side; m stays positive and `uphill` points outward */
  reversed?: boolean
  /**
   * DEFINES-SLOPE TEAR r3 (2026-08-12, Matt's "the high roof should extend out
   * over the roof below"): a FLYING RAKE piece — the upper sheet's continuation
   * past a tear, out to the tear rail's rake line (the rail gable edge's
   * overhang), riding the SAME plane as its parent face and clipped where the
   * surface meets the lower sheet. NOT part of the plan partition (it overlaps
   * the lower faces in plan, open air between) — partition-sum consumers must
   * skip flying faces.
   */
  flying?: boolean
  /** CORNER LAPS (2026-10-13): a steeper eave's continuation tucked UNDER the shallow
   *  overhang at an inside corner set to 'tuck' (cornerLap.ts) — also `flying` */
  tucked?: boolean
  /**
   * PHASE SPLIT round (docs/MODELER.md): set on a sub-face produced by clipping
   * against a phase region (phaseSplit.ts#splitRoofFacesByPhase) instead of the
   * roof element's own top-level phaseCreated/phaseDemolished. Absent on an
   * unsplit roof's faces — zero behavior change.
   */
  phaseOverride?: { phaseCreated?: PhaseId; phaseDemolished?: PhaseId }
}

export interface RoofSolveResult {
  eaveRing: Pt2[]
  faces: RoofFace[]
}

const EPS = 1e-9
const AREA_EPS = 1e-6
/** the shallowest pitch a sloping edge may carry, either way (the slope-0 guard) */
export const MIN_SLOPE_RISE = 0.25

function signedArea(ring: Pt2[]): number {
  let s = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
  }
  return s / 2
}

function lineIntersect(p1: Pt2, d1: Pt2, p2: Pt2, d2: Pt2): Pt2 | null {
  const cross = d1[0] * d2[1] - d1[1] * d2[0]
  if (Math.abs(cross) < EPS) return null
  const t = ((p2[0] - p1[0]) * d2[1] - (p2[1] - p1[1]) * d2[0]) / cross
  return [p1[0] + d1[0] * t, p1[1] + d1[1] * t]
}

/**
 * Clip a polygon to the half-plane f(p) ≥ 0 where f is LINEAR (Sutherland–Hodgman).
 * Works for any simple polygon; degenerate output rings are filtered by the caller.
 */
function clipHalfPlane(ring: Pt2[], f: (p: Pt2) => number): Pt2[] {
  const out: Pt2[] = []
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const cur = ring[i], prev = ring[(i + n - 1) % n]
    const fc = f(cur), fp = f(prev)
    if (fc >= -EPS) {
      if (fp < -EPS) {
        const t = fp / (fp - fc)
        out.push([prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t])
      }
      out.push([cur[0], cur[1]])
    } else if (fp >= -EPS) {
      const t = fp / (fp - fc)
      out.push([prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t])
    }
  }
  return out
}

/** Deduplicate near-coincident consecutive ring points. */
function cleanRing(ring: Pt2[]): Pt2[] {
  const out: Pt2[] = []
  for (const p of ring) {
    const last = out[out.length - 1]
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 1e-6) out.push(p)
  }
  if (out.length > 1) {
    const a = out[0], b = out[out.length - 1]
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) <= 1e-6) out.pop()
  }
  return dropSpikes(out)
}

/**
 * ZERO-WIDTH SPIKES (2026-10-05, Luisi roof ljEzPT8Tf8yw): the skeleton can hand back a face
 * ring that runs out along a ridge line and straight back (… [6.5,-6.6] → [-79.4,-6.6] →
 * [-31.5,-6.6] …) — zero area, so it passed the tiling audit, but each face built a sloped
 * sliver 45 ft past the house. Drop every vertex where the ring doubles back on itself
 * (collinear, reversing direction), repeating until none remain.
 */
function dropSpikes(ring: Pt2[]): Pt2[] {
  let out = ring
  for (let guard = 0; guard < ring.length && out.length > 3; guard++) {
    let hit = -1
    for (let i = 0; i < out.length; i++) {
      const a = out[(i + out.length - 1) % out.length], b = out[i], c = out[(i + 1) % out.length]
      const ux = b[0] - a[0], uy = b[1] - a[1], vx = c[0] - b[0], vy = c[1] - b[1]
      const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy)
      if (lu < 1e-9 || lv < 1e-9) { hit = i; break }
      const cross = (ux * vy - uy * vx) / (lu * lv), dot = (ux * vx + uy * vy) / (lu * lv)
      if (Math.abs(cross) < 1e-6 && dot < 0) { hit = i; break }
    }
    if (hit < 0) break
    out = out.filter((_, k) => k !== hit)
  }
  return out
}

/**
 * Solve the roof faces over a boundary ring.
 * @param boundary plan ring WITHOUT a duplicate closing point; any winding (normalized
 *   internally — edge specs must be in the SAME order as the input ring's edges)
 * @param edges per-edge specs, edges[i] = boundary[i] → boundary[i+1]
 * @param zPlateFt the wall-plate elevation (the z of every wall line)
 */
export function solveRoofFaces(boundary: Pt2[], edges: RoofEdgeSpec[], zPlateFt: number): RoofSolveResult {
  if (boundary.length < 3 || edges.length !== boundary.length) return { eaveRing: [], faces: [] }

  // REVERSED PITCH (2026-10-19, Matt): when EVERY sloping edge has a negative slope the
  // roof is the mirror image of the same roof pitched normally — same faces in plan,
  // flipped upside down about the plate (a butterfly, an inverted hip, a reversed shed).
  // Solve the normal roof and mirror it: z' = 2·plate − z, ascent turned around, so each
  // face still reads "rises at m along uphill" (m stays positive). Mixed signs have no
  // such mirror; roofSlopeProblem refuses them before they are stored.
  const slopedSpecs = edges.filter((e) => !e.gable)
  if (slopedSpecs.length && slopedSpecs.every((e) => e.slopeRise < 0)) {
    const normal = solveRoofFaces(boundary, edges.map((e) => (e.gable ? e : { ...e, slopeRise: -e.slopeRise })), zPlateFt)
    return {
      eaveRing: normal.eaveRing,
      faces: normal.faces.map((f) => {
        const z = f.zAt
        return { ...f, zAt: (p: Pt2) => 2 * zPlateFt - z(p), uphill: [-f.uphill[0], -f.uphill[1]] as Pt2, reversed: true }
      }),
    }
  }

  // normalize CCW (interior on the LEFT of travel) — reverse ring AND edge order
  let ring = boundary.map((p) => [p[0], p[1]] as Pt2)
  let specs = edges.slice()
  let reversed = false
  if (signedArea(ring) < 0) {
    reversed = true
    ring = [...ring].reverse()
    // original edge j (v_j → v_j+1) is edge (n-2-j) mod n of the REVERSED ring,
    // endpoints swapped. Note the -2: a plain array reversal maps to (n-1-k),
    // which ROTATED every per-edge spec (gable flags, slopes, overhangs) one
    // edge over on CW-drawn boundaries — a pre-existing ROOF-round bug, found
    // via the Forsyth shed (2026-08-10): its west bearing edge solved as if it
    // were the south gable.
    const m = specs.length
    const src = specs
    specs = src.map((_, k) => {
      const e = src[(m - 2 - k + m) % m]
      return { ...e, a: e.b, b: e.a }
    })
  }
  const n = ring.length

  // per-edge geometry: unit dir, inward normal (LEFT of travel for CCW)
  const dirs: Pt2[] = []
  const inward: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len < EPS) { dirs.push([1, 0]); inward.push([0, 1]); continue }
    dirs.push([dx / len, dy / len])
    inward.push([-dy / len, dx / len])
  }

  // eave ring: each edge line moved OUTWARD by its overhang, vertices re-intersected
  // (miter; translate fallback at near-parallel corners)
  const eaveRing: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const prev = (i + n - 1) % n
    const oPrev: Pt2 = [
      ring[prev][0] - inward[prev][0] * specs[prev].overhangFt,
      ring[prev][1] - inward[prev][1] * specs[prev].overhangFt,
    ]
    const oCur: Pt2 = [
      ring[i][0] - inward[i][0] * specs[i].overhangFt,
      ring[i][1] - inward[i][1] * specs[i].overhangFt,
    ]
    const hit = lineIntersect(oPrev, dirs[prev], oCur, dirs[i])
    eaveRing.push(hit ?? oCur)
  }

  // plane per non-gable edge: z(p) = zPlate + m · (signed distance from the WALL line,
  // positive toward the interior)
  const planes = specs.map((e, i) => {
    if (e.gable) return null
    const m = e.slopeRise / 12
    const a = ring[i], nrm = inward[i]
    return (p: Pt2) => zPlateFt + m * ((p[0] - a[0]) * nrm[0] + (p[1] - a[1]) * nrm[1])
  })

  // faces are built in NORMALIZED (CCW) edge order; remap edgeIndex back to the
  // caller's input order at every return so no consumer ever sees the internal
  // space (the recurring winding trap — see the RoofFace.edgeIndex doc)
  const toInputIndex = (k: number) => (reversed ? (n - 2 - k + n) % n : k)
  const remapToInput = (fs: RoofFace[]): RoofFace[] => {
    for (const f of fs) f.edgeIndex = toInputIndex(f.edgeIndex)
    return fs
  }

  const mkFace = (i: number, region: Pt2[]): RoofFace | null => {
    const zi = planes[i]
    if (!zi) return null
    const clean = cleanRing(region)
    if (clean.length < 3 || Math.abs(signedArea(clean)) <= AREA_EPS) return null
    return {
      edgeIndex: i, ring: clean, m: specs[i].slopeRise / 12, zAt: zi,
      origin: [ring[i][0], ring[i][1]], uphill: [inward[i][0], inward[i][1]],
      eaveDepthIn: specs[i].eaveStructureDepthIn ?? null,
      tails: specs[i].tails ?? false,
      overhangFt: specs[i].overhangFt,
      wallFt: specs[i].wallFt ?? 0,
      flatSoffit: specs[i].flatSoffit ?? false,
      frieze: specs[i].frieze ?? false,
    }
  }

  // WEIGHTED STRAIGHT SKELETON (the 2026-08-10 solver): valid only when every
  // non-gable edge has a real positive slope (a zero slope means an infinite
  // front speed — degenerate, envelope handles it as before). The WHOLE attempt
  // is exception-proof: this runs inside the model-generate worker, so any
  // uncaught throw here blanks the entire model (3D + every plan cut) — a bad
  // roof must only ever cost us the fallback, never the render (the Forsyth
  // shed-with-overhang regression, 2026-08-10).
  if (specs.some((e) => !e.gable) && specs.every((e) => e.gable || e.slopeRise > 0)) {
    let rings: (Pt2[] | null)[] | null = null
    try {
      const speeds = specs.map((e) => (e.gable ? 0 : 12 / e.slopeRise))
      const wallRings = computeSkeletonFaceRings(ring, speeds)
      rings = wallRings
      if (rings && specs.some((e) => Math.abs(e.overhangFt) > 1e-9)) {
        rings = extendFacesToEave(rings, ring, dirs, specs, eaveRing)
        if (rings && !facesTileRing(rings, eaveRing)) rings = null
        // CONSTRUCTIVE second chance (2026-08-12 for torn skeletons; un-gated
        // 2026-08-19): the ray-walk above assumes each face's gable-slide runs
        // sit at the ENDS of its boundary chain, so it also fails on a
        // perfectly healthy skeleton whenever a face touches an OVERHUNG gable
        // line mid-chain (Matt's L-roof: rake overhang on the shed's high-side
        // gable — the neighboring face meets that line between its ridge end
        // and valley end, the walk's rays shoot across other faces' bands, and
        // the whole roof used to dump to the ENVELOPE, whose adjacency-free
        // minimum swept the low plane 5 ft below the plate and read as "the
        // bearing edges flipped"). The band extension claims per-edge band
        // trapezoids segment-by-segment — mid-chain runs included — so try it
        // whenever the ray-walk failed; the tiling audit below still gates the
        // result, and a genuinely unsolvable extension falls to the envelope
        // exactly as before.
        if (!rings && wallRings) {
          rings = extendFacesByBands(wallRings, ring, dirs, inward, specs, eaveRing)
        }
      }
      if (rings && !facesTileRing(rings, eaveRing)) rings = null
    } catch {
      rings = null
    }
    if (rings) {
      const faces: RoofFace[] = []
      for (let i = 0; i < n; i++) {
        const r = rings[i]
        if (!r) continue
        const face = mkFace(i, r)
        if (face) faces.push(face)
      }
      if (faces.length) {
        appendFlyingRakes(faces, ring, dirs, inward, specs)
        return { eaveRing, faces: remapToInput(faces) }
      }
    }
  }

  // LOWER-ENVELOPE fallback (the pre-skeleton solver, exact for convex footprints):
  // face i = the region of the eave ring where plane i is the minimum
  const faces: RoofFace[] = []
  for (let i = 0; i < n; i++) {
    const zi = planes[i]
    if (!zi) continue
    let region = eaveRing.map((p) => [p[0], p[1]] as Pt2)
    for (let j = 0; j < n && region.length >= 3; j++) {
      const zj = planes[j]
      if (j === i || !zj) continue
      region = clipHalfPlane(region, (p) => zj(p) - zi(p))
    }
    const face = mkFace(i, region)
    if (face) faces.push(face)
  }
  return { eaveRing, faces: remapToInput(faces) }
}

/**
 * Extend each skeleton face polygon (solved over the WALL ring) outward to the eave
 * ring. The face's two lateral boundary lines through its wall corners stay straight
 * below the plate (the planes are linear), so the extension is: strip the wall edge
 * plus any gable-line slide runs at its ends, project the first interior arcs
 * outward to the eave ring, and take the eave-ring portion between the two hits.
 * Adjacent faces extend the SAME arc lines, so the band tiles without gaps. Any
 * geometric surprise returns null → envelope fallback.
 */
function extendFacesToEave(
  rings: (Pt2[] | null)[],
  ring: Pt2[],
  dirs: Pt2[],
  specs: RoofEdgeSpec[],
  eaveRing: Pt2[],
): (Pt2[] | null)[] | null {
  const n = ring.length
  const gableLines: { p: Pt2; d: Pt2 }[] = []
  for (let i = 0; i < n; i++) {
    if (specs[i].gable) gableLines.push({ p: ring[i], d: dirs[i] })
  }
  const onGableLine = (a: Pt2, b: Pt2) =>
    gableLines.some((g) =>
      Math.abs(g.d[0] * (a[1] - g.p[1]) - g.d[1] * (a[0] - g.p[0])) <= 1e-6 &&
      Math.abs(g.d[0] * (b[1] - g.p[1]) - g.d[1] * (b[0] - g.p[0])) <= 1e-6)

  const ne = eaveRing.length
  /** first eave-ring hit of the ray from P along u → { pt, seg, s } */
  const rayHit = (P: Pt2, u: Pt2): { pt: Pt2; seg: number; s: number } | null => {
    let best: { pt: Pt2; seg: number; s: number } | null = null
    let bestT = Infinity
    for (let j = 0; j < ne; j++) {
      const q0 = eaveRing[j], q1 = eaveRing[(j + 1) % ne]
      const ex = q1[0] - q0[0], ey = q1[1] - q0[1]
      const den = u[0] * ey - u[1] * ex
      if (Math.abs(den) < EPS) continue
      const t = ((q0[0] - P[0]) * ey - (q0[1] - P[1]) * ex) / den
      const s = ((q0[0] - P[0]) * u[1] - (q0[1] - P[1]) * u[0]) / den
      if (t < -1e-7 || s < -1e-7 || s > 1 + 1e-7) continue
      if (t < bestT) {
        bestT = t
        best = { pt: [P[0] + u[0] * t, P[1] + u[1] * t], seg: j, s }
      }
    }
    return best
  }

  const out: (Pt2[] | null)[] = []
  for (let f = 0; f < n; f++) {
    const pts = rings[f]
    if (!pts) { out.push(null); continue }
    const np = pts.length
    if (np < 3) return null
    // pts[0] = wall a, pts[1] = wall b, then the interior chain back toward a.
    // b-side: skip slide runs along gable wall lines
    let ib = 1
    while (ib + 1 < np && onGableLine(pts[ib], pts[ib + 1])) ib++
    // a-side: walk backward from pts[0] through gable-line slide runs
    let ka = 0
    while (((ka + np - 1) % np) > ib && onGableLine(pts[(ka + np - 1) % np], pts[ka])) {
      ka = (ka + np - 1) % np
    }
    // slide runs may consume the ENTIRE chain (a shed's one face is bounded by
    // gable lines on every non-wall side) — no interior arc to project outward,
    // so bail to the envelope solve (which handles exactly this shape correctly)
    if (ib >= np - 1) return null
    if (ka !== 0 && ib >= ka) return null
    const B = pts[ib]
    const bIn = pts[ib + 1]
    const A = pts[ka]
    const aIn = pts[ka === 0 ? np - 1 : ka - 1]
    const norm = (v: Pt2): Pt2 | null => {
      const l = Math.hypot(v[0], v[1])
      return l < EPS ? null : [v[0] / l, v[1] / l]
    }
    const uB = norm([B[0] - bIn[0], B[1] - bIn[1]])
    const uA = norm([A[0] - aIn[0], A[1] - aIn[1]])
    if (!uB || !uA) return null
    const hitB = rayHit(B, uB)
    const hitA = rayHit(A, uA)
    if (!hitB || !hitA) return null
    // assemble: interior chain B…A, then A_out, eave-ring walk (CCW), B_out
    const ext: Pt2[] = []
    for (let i = ib; ; i = (i + 1) % np) {
      ext.push(pts[i])
      if (i === ka) break
      if (ext.length > np) return null
    }
    ext.push(hitA.pt)
    let seg = hitA.seg
    let sPos = hitA.s
    let guard = ne + 1
    while (!(seg === hitB.seg && hitB.s >= sPos - 1e-7)) {
      ext.push(eaveRing[(seg + 1) % ne])
      seg = (seg + 1) % ne
      sPos = 0
      if (--guard < 0) return null
    }
    ext.push(hitB.pt)
    out.push(ext)
  }
  return out
}

/**
 * FLYING RAKES (DEFINES-SLOPE TEAR r3, 2026-08-12 — Matt: "the high roof over
 * the low roof overhang gets cut when it should extend out over the roof
 * below"): for every tear boundary lying on a GABLE edge's line where that
 * edge carries an overhang, the UPPER face's sheet continues past the tear —
 * a strip riding the parent's plane out to the rail's rake line, exactly like
 * a rake overhang at the perimeter. Run ends project along the parent ring's
 * adjacent arcs (the E4 ridge-extends-to-the-barge convention; perpendicular
 * where the run dies mid-segment), and the strip clips where the parent's
 * plane meets the lower sheet (z_f ≥ z_g), so a telescoping rake lands exactly
 * on the lower ridge. Pieces are marked `flying` and OVERLAP the lower faces
 * in plan by design — the partition below stays untouched; open air between.
 * A rail with NO overhang gets no strip: the tear stays a flush vertical step.
 */
function appendFlyingRakes(
  faces: RoofFace[],
  ring: Pt2[],
  dirs: Pt2[],
  inward: Pt2[],
  specs: RoofEdgeSpec[],
): void {
  const n = ring.length
  const rails: number[] = []
  for (let k = 0; k < n; k++) {
    if (specs[k].gable && Math.abs(specs[k].overhangFt) > 1e-9) rails.push(k)
  }
  if (!rails.length) return
  const onLineK = (p: Pt2, k: number) =>
    Math.abs(dirs[k][0] * (p[1] - ring[k][1]) - dirs[k][1] * (p[0] - ring[k][0])) <= 1e-6
  const base = faces.slice()
  for (const f of base) {
    const np = f.ring.length
    for (let i = 0; i < np; i++) {
      const P = f.ring[i], Q = f.ring[(i + 1) % np]
      const k = rails.find((r) => onLineK(P, r) && onLineK(Q, r))
      if (k === undefined) continue
      // the strip flies OUTWARD across the rail — require the outward side to
      // be the far side of this segment from f's interior (CCW ring: interior
      // LEFT of travel, so outward must be the RIGHT normal's side)
      const ex = Q[0] - P[0], ey = Q[1] - P[1]
      const len = Math.hypot(ex, ey)
      if (len < 1e-6) continue
      const rightN: Pt2 = [ey / len, -ex / len]
      if (rightN[0] * -inward[k][0] + rightN[1] * -inward[k][1] < 0.99) continue
      const oh = specs[k].overhangFt
      const out: Pt2 = [-inward[k][0] * oh, -inward[k][1] * oh]
      // collect EVERY lower-face overlap along this segment, then fly ONE strip
      // over their contiguous union — the parent's tear boundary is a single
      // ring segment (the skeleton slide has no event at partition breaks
      // below), so a per-overlap strip would never exact-key-match it and the
      // parent's sheet-edge curtain would still emit (the E5 census lesson)
      const segLen = len
      const stAlong = (p: Pt2) => ((p[0] - P[0]) * ex + (p[1] - P[1]) * ey) / segLen
      const lowers: { g: RoofFace; s0: number; s1: number }[] = []
      for (const g of base) {
        if (g === f) continue
        for (let j = 0; j < g.ring.length; j++) {
          const ov = collinearOverlap(P, Q, g.ring[j], g.ring[(j + 1) % g.ring.length])
          if (!ov) continue
          const mid: Pt2 = [(ov[0][0] + ov[1][0]) / 2, (ov[0][1] + ov[1][1]) / 2]
          if (f.zAt(mid) - g.zAt(mid) <= 1e-6) continue  // only the UPPER flies
          lowers.push({ g, s0: stAlong(ov[0]), s1: stAlong(ov[1]) })
        }
      }
      if (!lowers.length) continue
      const u0 = Math.min(...lowers.map((l) => l.s0))
      const u1 = Math.max(...lowers.map((l) => l.s1))
      // the union must be contiguous lower coverage — a hole in the middle is a
      // genuinely open boundary that must keep its sheet-edge band instead
      const covered = lowers.reduce((s, l) => s + (l.s1 - l.s0), 0)
      if (covered < (u1 - u0) - 1e-4) continue
      const ux = ex / segLen, uy = ey / segLen
      // snap union ends onto the ring vertices so the strip's tear edge shares
      // the parent segment's EXACT key (that identity is what suppresses the
      // parent's sheet-edge curtain and classifies the boundary as a seam)
      const A: Pt2 = u0 <= 1e-9 ? [P[0], P[1]] : [P[0] + ux * u0, P[1] + uy * u0]
      const B: Pt2 = u1 >= segLen - 1e-9 ? [Q[0], Q[1]] : [P[0] + ux * u1, P[1] + uy * u1]
      // outer corners: project a union end along the parent ring's adjacent
      // arc when the end IS a ring vertex with a non-collinear arc beyond;
      // otherwise square (perpendicular)
      const projEnd = (E: Pt2, ringV: Pt2, arcOther: Pt2): Pt2 => {
        const isV = Math.hypot(E[0] - ringV[0], E[1] - ringV[1]) <= 1e-6
        if (isV) {
          const u: Pt2 = [ringV[0] - arcOther[0], ringV[1] - arcOther[1]]
          const ul = Math.hypot(u[0], u[1])
          if (ul > 1e-9 && Math.abs((u[0] * dirs[k][1] - u[1] * dirs[k][0]) / ul) > 1e-4) {
            const q0: Pt2 = [ring[k][0] + out[0], ring[k][1] + out[1]]
            const hit = lineIntersect(E, [u[0] / ul, u[1] / ul], q0, dirs[k])
            if (hit) return hit
          }
        }
        return [E[0] + out[0], E[1] + out[1]]
      }
      const prev = f.ring[(i - 1 + np) % np]
      const next = f.ring[(i + 2) % np]
      const outA = projEnd(A, P, prev)
      const outB = projEnd(B, Q, next)
      // clip where the parent surface meets EACH lower sheet (all linear).
      // CCW (parent rings are CCW; the strip hangs off the segment's right)
      let quad: Pt2[] = [[A[0], A[1]], outA, outB, [B[0], B[1]]]
      for (const l of lowers) {
        if (quad.length < 3) break
        quad = clipHalfPlane(quad, (p) => f.zAt(p) - l.g.zAt(p) + 1e-9)
      }
      const clean = cleanRing(quad)
      if (clean.length < 3 || Math.abs(signedArea(clean)) <= AREA_EPS) continue
      faces.push({
        edgeIndex: f.edgeIndex, ring: clean, m: f.m, zAt: f.zAt,
        origin: [f.origin[0], f.origin[1]], uphill: [f.uphill[0], f.uphill[1]],
        // the strip is pure overhang zone — it thins by the PARENT face's eave
        // structure depth exactly like the fixed rake band beside it (r4,
        // Matt's "the depth reduction isn't extending onto the overlap")
        eaveDepthIn: f.eaveDepthIn ?? null, tails: false, overhangFt: f.overhangFt, wallFt: f.wallFt,
        flatSoffit: false,
        frieze: false,
        flying: true,
      })
    }
  }
}

// (skeletonHasTear, the band extension's original tear-only gate, was removed
// 2026-08-19 when the gate came off — the band extension now runs whenever the
// ray-walk fails, torn or not.)

/** the overlapping sub-segment of two collinear segments (returned in P→Q
 *  order), or null — shared plan boundary detection for TEARS, whose two sides
 *  split at different event points so endpoints never match exactly. Exported
 *  for generate3d's tear step-gap pass and skeleton.ts's step classification. */
export function collinearOverlap(P: Pt2, Q: Pt2, R: Pt2, S: Pt2): [Pt2, Pt2] | null {
  const dx = Q[0] - P[0], dy = Q[1] - P[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-6) return null
  const ux = dx / len, uy = dy / len
  const off = (p: Pt2) => Math.abs(ux * (p[1] - P[1]) - uy * (p[0] - P[0]))
  if (off(R) > 1e-6 || off(S) > 1e-6) return null
  const st = (p: Pt2) => (p[0] - P[0]) * ux + (p[1] - P[1]) * uy
  const lo = Math.max(0, Math.min(st(R), st(S)))
  const hi = Math.min(len, Math.max(st(R), st(S)))
  if (hi - lo <= 1e-4) return null
  return [[P[0] + ux * lo, P[1] + uy * lo], [P[0] + ux * hi, P[1] + uy * hi]]
}

/**
 * TEAR-AWARE eave extension (2026-08-12, the telescoping-gable round — see the
 * call site). CONSTRUCTIVE, no ray-walk: the overhang band decomposes into one
 * trapezoid per boundary edge (wall segment ↔ its eave-ring image, mitered at
 * the ring vertices — exactly how the eave ring itself was built). Each face
 * claims the band pieces along its own wall-line runs: a non-gable edge's band
 * belongs to its own face; a gable edge's band splits between the faces that
 * SLIDE along it, at the outward projections of their run endpoints — a span
 * endpoint projects to the eave ring's miter vertex, an interior arc endpoint
 * (a ridge end on the gable line) projects along its own arc's line (the E4
 * ridge-extends-to-the-barge convention, so adjacent faces split identically).
 * Runs are clipped to each edge's span, so TEAR stretches (slide runs on a
 * gable line's extension past its endpoints, with another face on the far
 * side) receive no band and stay exactly where the skeleton put them. The
 * caller audits the result with the same tiling check as the primary path.
 */
function extendFacesByBands(
  rings: (Pt2[] | null)[],
  ring: Pt2[],
  dirs: Pt2[],
  inward: Pt2[],
  specs: RoofEdgeSpec[],
  eaveRing: Pt2[],
): (Pt2[] | null)[] | null {
  const n = ring.length
  const banded: number[] = []
  for (let k = 0; k < n; k++) if (Math.abs(specs[k].overhangFt) > 1e-9) banded.push(k)
  if (!banded.length) return rings

  const spanLens = ring.map((p, k) => {
    const q = ring[(k + 1) % n]
    return Math.hypot(q[0] - p[0], q[1] - p[1])
  })
  const onLineK = (p: Pt2, k: number) =>
    Math.abs(dirs[k][0] * (p[1] - ring[k][1]) - dirs[k][1] * (p[0] - ring[k][0])) <= 1e-6
  const stationOf = (p: Pt2, k: number) =>
    (p[0] - ring[k][0]) * dirs[k][0] + (p[1] - ring[k][1]) * dirs[k][1]

  // ---- REFLEX-CORNER BAND OVERLAP (2026-08-12 r2, Matt's "thin vertical roof
  // + angled cut backs" report): at a reentrant footprint corner where BOTH
  // edges carry overhangs, the two band trapezoids overlap in a wedge. The
  // eave-ring-style miter split handed the HIGHER surface a floating sliver
  // over the lower roof (the upper gable's rake band cutting diagonally into
  // the telescope's eave band). Resolve by surface height instead: the LOWER
  // face's band wraps SQUARE to the corner's wall line (a low eave dies at the
  // wall, full depth), and the HIGHER edge's effective span starts where the
  // lower band's outer line crosses it — which is exactly the eave ring's own
  // corner, so its projection target is unchanged. Ties (same plane both
  // sides) keep the miter.
  const effStart = ring.map(() => 0)
  const effEnd = spanLens.slice()
  // adjacent-edge index when this end wraps along the corner's WALL line (the
  // lower side of a reflex banded corner) — -1 = normal miter behavior. The
  // wrap follows the adjacent edge's ACTUAL line, not a synthetic
  // perpendicular: at drawing tolerance the rail is fractionally off-square,
  // and the flying-rake union scan needs the wrapped band edge to be exactly
  // collinear with the tear boundary it abuts.
  const wrapStart = ring.map(() => -1)
  const wrapEnd = ring.map(() => -1)
  {
    // relative bearing-plane z (plate = 0) for face f — every face ring belongs
    // to a non-gable edge, so its plane always exists
    const zRel = (f: number, p: Pt2) =>
      (specs[f].slopeRise / 12) * ((p[0] - ring[f][0]) * inward[f][0] + (p[1] - ring[f][1]) * inward[f][1])
    /** the face whose ring slides along edge k's line within the contested
     *  station window [w0, w1] (the wedge stretch) — the band claimant there */
    const cornerFace = (k: number, w0: number, w1: number): number | null => {
      for (let f = 0; f < n; f++) {
        const pts = rings[f]
        if (!pts) continue
        for (let i = 0; i < pts.length; i++) {
          const P = pts[i], Q = pts[(i + 1) % pts.length]
          if (!onLineK(P, k) || !onLineK(Q, k)) continue
          const lo = Math.min(stationOf(P, k), stationOf(Q, k))
          const hi = Math.max(stationOf(P, k), stationOf(Q, k))
          if (Math.min(hi, w1) - Math.max(lo, w0) > 1e-5) return f
        }
      }
      return null
    }
    const isReflex = (v: number) => {
      const pe = (v + n - 1) % n
      return dirs[pe][0] * dirs[v][1] - dirs[pe][1] * dirs[v][0] < -1e-9
    }
    for (const k of banded) {
      for (const end of [0, 1] as const) {
        const v = end === 0 ? k : (k + 1) % n           // the corner vertex
        const adj = end === 0 ? (k + n - 1) % n : (k + 1) % n
        if (!isReflex(v) || Math.abs(specs[adj].overhangFt) <= 1e-9) continue
        // where the ADJACENT band's outer (eave) line crosses my wall line —
        // the contested stretch of my span
        const adjEavePt: Pt2 = [ring[adj][0] - inward[adj][0] * specs[adj].overhangFt,
          ring[adj][1] - inward[adj][1] * specs[adj].overhangFt]
        const cross = lineIntersect(adjEavePt, dirs[adj], ring[k], dirs[k])
        if (!cross) continue
        const sC = stationOf(cross, k)
        const w0 = end === 0 ? 0 : Math.max(0, Math.min(spanLens[k], sC))
        const w1 = end === 0 ? Math.max(0, Math.min(spanLens[k], sC)) : spanLens[k]
        if (w1 - w0 <= 1e-5) continue
        const fMine = cornerFace(k, w0, w1)
        const fAdjSide = cornerFace(adj,
          ...(end === 0
            ? [Math.max(0, spanLens[adj] - specs[k].overhangFt * 2), spanLens[adj]] as const
            : [0, Math.min(spanLens[adj], specs[k].overhangFt * 2)] as const))
        if (fMine == null || fAdjSide == null || fMine === fAdjSide) continue
        const probe: Pt2 = [
          ring[v][0] - inward[k][0] * specs[k].overhangFt * 0.5 - inward[adj][0] * specs[adj].overhangFt * 0.5,
          ring[v][1] - inward[k][1] * specs[k].overhangFt * 0.5 - inward[adj][1] * specs[adj].overhangFt * 0.5,
        ]
        const dz = zRel(fMine, probe) - zRel(fAdjSide, probe)
        if (dz > 1e-6) {
          // I'm the HIGHER side: my band yields the wedge — span shrinks to the
          // eave-ring corner's own station
          if (end === 0) effStart[k] = Math.max(effStart[k], w1)
          else effEnd[k] = Math.min(effEnd[k], w0)
        } else if (dz < -1e-6) {
          // I'm the LOWER side: my band wraps along the corner's wall line
          if (end === 0) wrapStart[k] = adj
          else wrapEnd[k] = adj
        }
      }
    }
  }

  /** project a run endpoint on edge k's wall line out to edge k's EAVE line */
  const projToEave = (k: number, P: Pt2, u: Pt2 | null): Pt2 => {
    const s = stationOf(P, k)
    const oh0 = specs[k].overhangFt
    const alongWall = (adj: number, corner: Pt2): Pt2 => {
      const q0: Pt2 = [ring[k][0] - inward[k][0] * oh0, ring[k][1] - inward[k][1] * oh0]
      return lineIntersect(corner, dirs[adj], q0, dirs[k])
        ?? [P[0] - inward[k][0] * oh0, P[1] - inward[k][1] * oh0]
    }
    if (s <= effStart[k] + 1e-5) {
      return wrapStart[k] >= 0 ? alongWall(wrapStart[k], ring[k]) : eaveRing[k]
    }
    if (s >= effEnd[k] - 1e-5) {
      return wrapEnd[k] >= 0 ? alongWall(wrapEnd[k], ring[(k + 1) % n]) : eaveRing[(k + 1) % n]
    }
    const oh = specs[k].overhangFt
    const q0: Pt2 = [ring[k][0] - inward[k][0] * oh, ring[k][1] - inward[k][1] * oh]
    if (u) {
      const ul = Math.hypot(u[0], u[1])
      if (ul > 1e-9) {
        const un: Pt2 = [u[0] / ul, u[1] / ul]
        // near-parallel arcs (a tear continuing along the same line) fall through
        // to the perpendicular projection below
        if (Math.abs(un[0] * dirs[k][1] - un[1] * dirs[k][0]) > 1e-4) {
          const hit = lineIntersect(P, un, q0, dirs[k])
          if (hit) return hit
        }
      }
    }
    return [P[0] - inward[k][0] * oh, P[1] - inward[k][1] * oh]
  }

  const out: (Pt2[] | null)[] = []
  for (let f = 0; f < n; f++) {
    const pts = rings[f]
    if (!pts) { out.push(null); continue }
    const np = pts.length
    const newPts: Pt2[] = []
    for (let i = 0; i < np; i++) {
      const P = pts[i], Q = pts[(i + 1) % np]
      newPts.push([P[0], P[1]])
      // every banded-edge run this segment carries, clipped to the edge's span,
      // in travel order (collinear split edges can chain along one segment)
      const runs: { k: number; t0: number; t1: number }[] = []
      for (const k of banded) {
        if (!onLineK(P, k) || !onLineK(Q, k)) continue
        const sP = stationOf(P, k), sQ = stationOf(Q, k)
        if (Math.abs(sQ - sP) <= 1e-9) continue
        const lo = Math.max(Math.min(sP, sQ), effStart[k])
        const hi = Math.min(Math.max(sP, sQ), effEnd[k])
        if (hi - lo <= 1e-5) continue
        const a = (lo - sP) / (sQ - sP), b = (hi - sP) / (sQ - sP)
        runs.push({ k, t0: Math.min(a, b), t1: Math.max(a, b) })
      }
      runs.sort((x, y) => x.t0 - y.t0)
      for (const r of runs) {
        const atT = (t: number): Pt2 => [P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t]
        const S = r.t0 <= 1e-9 ? P : atT(r.t0)
        const E = r.t1 >= 1 - 1e-9 ? Q : atT(r.t1)
        // arc directions only exist where the run endpoint IS a ring vertex —
        // interior split points sit at span boundaries and project to miters
        const prev = pts[(i - 1 + np) % np]
        const next = pts[(i + 2) % np]
        const uS: Pt2 | null = r.t0 <= 1e-9 ? [P[0] - prev[0], P[1] - prev[1]] : null
        const uE: Pt2 | null = r.t1 >= 1 - 1e-9 ? [next[0] - Q[0], next[1] - Q[1]] : null
        if (r.t0 > 1e-9) newPts.push(S)
        newPts.push(projToEave(r.k, S, uS))
        newPts.push(projToEave(r.k, E, uE))
        if (r.t1 < 1 - 1e-9) newPts.push(E)
      }
    }
    const clean = removeSpikes(cleanRing(newPts))
    if (clean.length < 3) return null
    out.push(clean)
  }
  return out
}

/**
 * Remove zero-width folds (spikes) from a ring: a vertex whose two segments are
 * collinear and OPPOSITE-direction pinches the polygon to zero width — the band
 * splice produces one at a reflex corner where a face's band strip and its
 * follow-through sliver meet along the same line (the union's true boundary
 * runs straight through). Area is unchanged; only the degenerate pinch goes.
 */
function removeSpikes(ring: Pt2[]): Pt2[] {
  const pts = ring.map((p) => [p[0], p[1]] as Pt2)
  let changed = true
  while (changed && pts.length >= 3) {
    changed = false
    for (let i = 0; i < pts.length; i++) {
      const P = pts[(i - 1 + pts.length) % pts.length]
      const V = pts[i]
      const N = pts[(i + 1) % pts.length]
      const ax = V[0] - P[0], ay = V[1] - P[1]
      const bx = N[0] - V[0], by = N[1] - V[1]
      // relative tolerance too — a long drift-angled fold has |cross| far above
      // any absolute epsilon while its turn angle is microscopic
      const la = Math.hypot(ax, ay), lb = Math.hypot(bx, by)
      if ((Math.abs(ax * by - ay * bx) <= Math.max(1e-9, la * lb * 1e-7)) && ax * bx + ay * by < 0) {
        pts.splice(i, 1)
        changed = true
        break
      }
    }
  }
  return pts
}

/** verify the solved faces PARTITION the eave ring (area sum matches) */
function facesTileRing(rings: (Pt2[] | null)[], eaveRing: Pt2[]): boolean {
  if (eaveRing.length < 3) return false
  const target = Math.abs(signedArea(eaveRing))
  let sum = 0
  for (const r of rings) {
    if (!r) continue
    if (r.length < 3) return false
    for (const p of r) {
      if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) return false
    }
    sum += Math.abs(signedArea(r))
  }
  return Math.abs(sum - target) <= Math.max(1e-6 * target, 1e-6)
}

/**
 * Resolve per-edge specs from a boundary Path2D's nodes + the instance defaults —
 * the bridge between the kernel's PathNode.edge attrs and the solver.
 */
export function edgeSpecsFromNodes(
  nodes: { pt: Pt2; edge?: { slopeRise?: number; overhangFt?: number; gable?: boolean; eaveStructureDepthIn?: number; tails?: boolean; fascia?: boolean; gutter?: boolean; barge?: boolean; flatSoffit?: boolean; frieze?: boolean } }[],
  defaults: { slopeRise: number; overhangFt: number; flat?: boolean; eaveStructureDepthIn?: number | null; tailsDefault?: boolean; fasciaDefault?: boolean; gutterDefault?: boolean; bargeDefault?: boolean; flatSoffitDefault?: boolean; friezeDefault?: boolean },
): RoofEdgeSpec[] {
  const n = nodes.length
  return nodes.map((node, i) => {
    const gable = defaults.flat ? false : (node.edge?.gable ?? false)
    let slopeRise = defaults.flat ? 0 : (node.edge?.slopeRise ?? defaults.slopeRise)
    // REVERSED PITCH: a negative slope is never shallower than the positive floor
    if (slopeRise < 0 && slopeRise > -MIN_SLOPE_RISE) slopeRise = -MIN_SLOPE_RISE
    // a reversed edge is its face's HIGH side: nothing drains there, and tails / a flat
    // soffit describe a low eave — they stay off on it
    const high = !gable && slopeRise < 0
    return {
      a: [node.pt[0], node.pt[1]] as Pt2,
      b: [nodes[(i + 1) % n].pt[0], nodes[(i + 1) % n].pt[1]] as Pt2,
      slopeRise,
      overhangFt: node.edge?.overhangFt ?? defaults.overhangFt,
      gable,
      eaveStructureDepthIn: node.edge?.eaveStructureDepthIn ?? defaults.eaveStructureDepthIn ?? null,
      tails: high ? false : (node.edge?.tails ?? defaults.tailsDefault ?? false),
      fascia: node.edge?.fascia ?? defaults.fasciaDefault ?? false,
      gutter: high ? false : (node.edge?.gutter ?? defaults.gutterDefault ?? false),
      barge: node.edge?.barge ?? defaults.bargeDefault ?? false,
      flatSoffit: high ? false : (node.edge?.flatSoffit ?? defaults.flatSoffitDefault ?? false),
      frieze: high ? false : (node.edge?.frieze ?? defaults.friezeDefault ?? false),
    }
  })
}

/**
 * The roof's effective slope/overhang defaults (ROOF round r2): per-roof INSTANCE
 * overrides (nullable — Matt's "can't edit the pitch" fix) over the TYPE standards.
 * The instance `overhangFt` key shadows the type's in resolved params, so this is the
 * ONE correct read — never `el.params.overhangFt` alone. Per-edge node attrs still
 * override both, downstream in edgeSpecsFromNodes.
 */
export function roofDefaultsFor(el: ResolvedElement): {
  slopeRise: number
  overhangFt: number
  eaveStructureDepthIn: number | null
  tailsDefault: boolean
  fasciaDefault: boolean
  gutterDefault: boolean
  bargeDefault: boolean
  flatSoffitDefault: boolean
  friezeDefault: boolean
} {
  const t = el.typeParams ?? {}
  // E1 eave crop (an INSTANCE eave-styling param since the eave-styling round —
  // the type stays the structure standard): a choice param stored as a STRING
  // ('' = no crop) — parse here so every consumer sees inches-or-null. Guard ''
  // explicitly: Number('') is 0, which would read as "crop the full core depth".
  const rawEave = el.params.eaveStructureDepthIn
  const eaveNum = rawEave === '' || rawEave == null ? NaN : Number(rawEave)
  return {
    slopeRise: (el.params.slopeRise as number | null) ?? (t.defaultSlopeRise as number) ?? 6,
    overhangFt: (el.params.overhangFt as number | null) ?? (t.overhangFt as number) ?? 0,
    eaveStructureDepthIn: Number.isFinite(eaveNum) ? eaveNum : null,
    // all-edges defaults (instance eave styling) — per-edge node attrs override
    // these downstream
    tailsDefault: el.params.tailsDefault === true,
    fasciaDefault: el.params.fasciaDefault === true,
    gutterDefault: el.params.gutterDefault === true,
    bargeDefault: el.params.bargeDefault === true,
    // FLAT SOFFIT + CORBELS round: the instance soffit style, per-edge flatSoffit over it
    flatSoffitDefault: el.params.soffitStyle === 'flat',
    friezeDefault: el.params.friezeDefault === true,
  }
}

export interface ResolvedRoofSolve {
  ring: Pt2[]
  specs: RoofEdgeSpec[]
  zPlateFt: number
  flat: boolean
  eaveRing: Pt2[]
  faces: RoofFace[]
  /** CORNER LAPS (2026-10-13): edges whose kind the corner-tuck cleanup decides (the
   *  shallow eave carried on to where it meets the steep roof) — the skeleton honors them */
  edgeOverrides?: { a: Pt2; b: Pt2; kind: 'eave'; joinRun: boolean; edgeIndex: number }[]
  /** PER-EDGE BEARING HEIGHT: each edge's bearing height above (+) / below (−) `zPlateFt`,
   *  present only when some edge bears off the roof's plate (a face's own bearing height is
   *  always `zAt(origin)`) */
  plateOffsetFt?: number[]
}

/**
 * PER-EDGE BEARING HEIGHT (2026-10-13 spike): the adjusted ring + specs for edges that bear
 * off the roof's plate (`edge.plateOffsetFt`, feet, + = higher). Reference height = the
 * highest bearing (≥ the plate); each SLOPED edge's line moves inward by (reference − its
 * bearing) ÷ its slope and its overhang grows by the same, so its plane and its eave line are
 * unchanged. Gable edges don't move (they carry no plane). Null when no edge is offset or the
 * adjusted ring would fold over (a run lowered past its own width) — the roof then solves as
 * drawn, with every edge at the plate.
 */
function virtualBearing(
  ring: Pt2[], specs: RoofEdgeSpec[], nodes: { edge?: { plateOffsetFt?: unknown } }[],
): { ring: Pt2[]; specs: RoofEdgeSpec[]; topFt: number; offsets: number[] } | null {
  const n = ring.length
  const off = specs.map((sp, i) => {
    const v = nodes[i]?.edge?.plateOffsetFt
    return !sp.gable && sp.slopeRise > 0 && typeof v === 'number' && Number.isFinite(v) ? v : 0
  })
  if (!off.some((v) => Math.abs(v) > 1e-9)) return null
  const top = Math.max(0, ...off)
  let area = 0
  for (let i = 0; i < n; i++) { const a = ring[i], b = ring[(i + 1) % n]; area += a[0] * b[1] - b[0] * a[1] }
  const ccw = area > 0
  const lines = ring.map((a, i) => {
    const b = ring[(i + 1) % n]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    const d: Pt2 = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]
    const inw: Pt2 = ccw ? [-d[1], d[0]] : [d[1], -d[0]]
    const shift = specs[i].gable || !(specs[i].slopeRise > 0) ? 0 : (top - off[i]) / (specs[i].slopeRise / 12)
    return { p: [a[0] + inw[0] * shift, a[1] + inw[1] * shift] as Pt2, d, shift }
  })
  const out: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const L0 = lines[(i + n - 1) % n], L1 = lines[i]
    const hit = lineIntersect(L0.p, L0.d, L1.p, L1.d)
    out.push(hit ?? L1.p)
  }
  let area2 = 0
  for (let i = 0; i < n; i++) { const a = out[i], b = out[(i + 1) % n]; area2 += a[0] * b[1] - b[0] * a[1] }
  if ((area2 > 0) !== ccw || Math.abs(area2) < 1e-6) return null
  // every adjusted edge must still run the way the drawn one does (no folded-over run)
  for (let i = 0; i < n; i++) {
    const a = out[i], b = out[(i + 1) % n]
    if ((b[0] - a[0]) * lines[i].d[0] + (b[1] - a[1]) * lines[i].d[1] < -1e-9) return null
  }
  const vSpecs = specs.map((sp, i) => ({ ...sp, a: out[i], b: out[(i + 1) % n], overhangFt: sp.overhangFt + lines[i].shift }))
  return { ring: out, specs: vSpecs, topFt: top, offsets: off }
}

/**
 * The ONE element → solved-faces bridge, shared by the 3D generator, the skeleton
 * API, and walls-attach-to-roof — so every consumer reads identical planes. Defaults
 * (slope, overhang) come from the roof TYPE (ROOF round — firm standards, per the
 * types doctrine; resolved params already merge type-over-schema-default, and
 * pre-move instance-stored defaults simply stop contributing). Flat style solves as
 * ONE horizontal face over the eave ring.
 */
export function solveRoofForElement(el: ResolvedElement): ResolvedRoofSolve | null {
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.nodes || boundary.nodes.length < 3 || !el.level) return null
  const zPlateFt = el.level.elevationFt + ((el.params.plateHeightFt as number) || 0)
  const flat = el.params.style === 'flat'
  const ring = boundary.nodes.map((n) => [n.pt[0], n.pt[1]] as Pt2)
  const defaults = roofDefaultsFor(el)
  const specs = edgeSpecsFromNodes(boundary.nodes as never, { ...defaults, flat })
  // ROOF BEARING WALLS (2026-10-05, Matt): the bearing line is the INTERIOR face of the
  // stud, so an edge's overhang counts from the OUTSIDE face of STUD of the wall standing
  // under it (2026-10-12, Matt: never the finishes — a zero eave stops at the framing),
  // eaves and rakes alike. `wallFt` keeps the finished face for the show members.
  const wallFt = el.roofBearingWallFt
  const faceFt = el.roofBearingWallFaceFt
  if (wallFt) {
    specs.forEach((sp, i) => {
      const w = wallFt[i] ?? 0
      if (w > 0) { sp.overhangFt += w; sp.wallFt = Math.max(w, faceFt?.[i] ?? w) }
    })
  }
  if (flat) {
    const solved = solveRoofFaces(ring, specs.map((s) => ({ ...s, slopeRise: 0, gable: false })), zPlateFt)
    const faces: RoofFace[] = solved.eaveRing.length >= 3
      ? [{
          edgeIndex: 0, ring: solved.eaveRing, m: 0, zAt: () => zPlateFt,
          origin: [ring[0][0], ring[0][1]], uphill: [0, 0],
          // flat solves as ONE synthetic face, so per-edge eave values can't apply —
          // the type/instance default governs the whole eave band (documented v1)
          eaveDepthIn: defaults.eaveStructureDepthIn,
        }]
      : []
    return { ring, specs, zPlateFt, flat, eaveRing: solved.eaveRing, faces }
  }
  // PER-EDGE BEARING HEIGHT (2026-10-13): an edge may bear higher or lower than the roof's
  // plate (`edge.plateOffsetFt`) — e.g. a cross gable whose eaves sit above or below the main
  // body's. A plane raised by Δ at slope m is the same plane as one at a common reference
  // height with its bearing line moved Δ/m outward — so the SOLVER runs on an adjusted ring
  // (the highest bearing as reference, every other sloped line moved inward by its drop ÷
  // slope, its overhang grown by the same so the eave stays put). Everything downstream sees
  // the DRAWN ring, the real overhangs and each face anchored on its own bearing line
  // (`origin`, `zAt(origin)` = that edge's bearing height); `plateOffsetFt` says per edge.
  const vb = virtualBearing(ring, specs, boundary.nodes as never)
  const solved = vb ? solveRoofFaces(vb.ring, vb.specs, zPlateFt + vb.topFt) : solveRoofFaces(ring, specs, zPlateFt)
  const faces = vb
    ? solved.faces.map((f) => ({ ...f, origin: [ring[f.edgeIndex][0], ring[f.edgeIndex][1]] as Pt2, overhangFt: specs[f.edgeIndex].overhangFt }))
    : solved.faces
  const out: ResolvedRoofSolve = { ring, specs, zPlateFt, flat, eaveRing: solved.eaveRing, faces, ...(vb ? { plateOffsetFt: vb.offsets } : {}) }
  // CORNER LAPS: inside corners set to 'tuck' carry the steeper eave on under the shallow one
  // (and trims the shallow sheet back to its own eave line there — no wedge past its fascia)
  try {
    const tk = applyCornerTucks(el, out)
    out.faces = tk.faces
    if (tk.overrides.length) out.edgeOverrides = tk.overrides
  } catch { /* the roof still solves */ }
  return out
}

/**
 * REVERSED PITCH (2026-10-19): why this roof can't be built as set, or null when it can —
 * the one check every slope entry point runs before storing (the outline editor's Finish,
 * Edge properties, Roof Settings). `override` tries a boundary and / or roof slope before
 * they are stored. Only roofs with a negative slope are judged, so every normally pitched
 * roof behaves exactly as before. A reversed roof is the mirror of a normal one, so its
 * sloping edges must ALL be negative, and the mirrored solve must fill the outline.
 */
export function roofSlopeProblem(el: ResolvedElement, override: { boundary?: Path2D | null; slopeRise?: number | null } = {}): string | null {
  const params = { ...el.params }
  if (override.boundary !== undefined) params.boundary = override.boundary as never
  if (override.slopeRise !== undefined) params.slopeRise = override.slopeRise as never
  if (params.style === 'flat') return null
  const boundary = params.boundary as Path2D | null
  if (!boundary?.nodes || boundary.nodes.length < 3) return null
  const trial = { ...el, params } as ResolvedElement
  const specs = edgeSpecsFromNodes(boundary.nodes as never, roofDefaultsFor(trial))
  const sloped = specs.filter((s) => !s.gable)
  if (!sloped.some((s) => s.slopeRise < 0)) return null
  if (sloped.some((s) => s.slopeRise > 0)) {
    return "This roof mixes reversed (negative) and normal slopes on its sloping edges, which can't make one roof. Make every sloping edge negative or every one positive, or adjust the outline, and try again."
  }
  let solved: ResolvedRoofSolve | null = null
  try { solved = solveRoofForElement(trial) } catch { solved = null }
  const faces = (solved?.faces ?? []).filter((f) => !f.flying)
  if (!solved || !faces.length || !facesTileRing(faces.map((f) => f.ring), solved.eaveRing)) {
    return "The reversed slopes don't make a roof over this outline. Adjust the outline (or the edges that define slope) and try again."
  }
  return null
}
